import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

interface SubjectCount { subject: string; count: number }

interface PeriodStats {
  period: string;
  label: string;
  questionsAdded: SubjectCount[];
  questionsAddedTotal: number;
  uniqueQuestionsAttempted: SubjectCount[];
  uniqueQuestionsAttemptedTotal: number;
}

interface TimeseriesBucket {
  date: string;
  maths: number;
  physics: number;
  chemistry: number;
}

interface QuestionStatsTabProps {
  data?: { periods: PeriodStats[] };
  isLoading: boolean;
  timeseries?: TimeseriesBucket[];
  timeseriesLoading: boolean;
  /** Label for the active range, e.g. "4 Weeks". */
  rangeLabel: string;
  /** True when the range is large enough that we should bucket daily data
   *  into weeks for the chart (mirrors the convention used by UsageTab). */
  isWeekly: boolean;
  /** Active status filter — '' (all), 'live', or 'draft'. */
  status: string;
}

const SUBJECT_META = [
  { key: 'maths',     label: 'Maths',     color: '#3b82f6' },
  { key: 'physics',   label: 'Physics',   color: '#8b5cf6' },
  { key: 'chemistry', label: 'Chemistry', color: '#10b981' },
] as const;

const TOOLTIP_CONTENT_STYLE = {
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  color: 'var(--text-primary)',
};

function formatDay(date: string): string {
  const d = new Date(date + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatWeekRange(date: string): string {
  const start = new Date(date + 'T00:00:00Z');
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${s} – ${e}`;
}

/** Bucket daily counts into 7-day weeks keyed by the chunk's first date. */
function bucketWeekly(daily: TimeseriesBucket[]): TimeseriesBucket[] {
  const out: TimeseriesBucket[] = [];
  for (let i = 0; i < daily.length; i += 7) {
    const chunk = daily.slice(i, i + 7);
    out.push({
      date: chunk[0].date,
      maths: chunk.reduce((s, p) => s + p.maths, 0),
      physics: chunk.reduce((s, p) => s + p.physics, 0),
      chemistry: chunk.reduce((s, p) => s + p.chemistry, 0),
    });
  }
  return out;
}

export function QuestionStatsTab(props: QuestionStatsTabProps) {
  if (props.isLoading) return <div className="loading">Loading question stats...</div>;
  if (!props.data) return null;

  return (
    <div className="question-stats">
      <StatsSection title="Questions Added" periods={props.data.periods} getSubjects={p => p.questionsAdded} getTotal={p => p.questionsAddedTotal} />
      <StatsSection title="Unique Questions Attempted" periods={props.data.periods} getSubjects={p => p.uniqueQuestionsAttempted} getTotal={p => p.uniqueQuestionsAttemptedTotal} />
      <QuestionsCreatedChart
        buckets={props.timeseries}
        isLoading={props.timeseriesLoading}
        rangeLabel={props.rangeLabel}
        isWeekly={props.isWeekly}
        status={props.status}
      />
    </div>
  );
}

function StatsSection({ title, periods, getSubjects, getTotal }: {
  title: string;
  periods: PeriodStats[];
  getSubjects: (p: PeriodStats) => SubjectCount[];
  getTotal: (p: PeriodStats) => number;
}) {
  return (
    <div className="chart-card" style={{ marginBottom: 24 }}>
      <h3>{title}</h3>
      <div className="qs-table-wrap">
        <table className="dt dt--compact">
          <thead>
            <tr>
              <th>Period</th>
              <th>Total</th>
              <th>Maths</th>
              <th>Physics</th>
              <th>Chemistry</th>
              <th>Biology</th>
            </tr>
          </thead>
          <tbody>
            {periods.map(p => {
              const bySubject = Object.fromEntries(getSubjects(p).map(s => [s.subject, s.count]));
              return (
                <tr key={p.period}>
                  <td style={{ fontWeight: 500 }}>{p.label}</td>
                  <td style={{ fontWeight: 600 }}>{getTotal(p)}</td>
                  <td>{bySubject['maths'] || 0}</td>
                  <td>{bySubject['physics'] || 0}</td>
                  <td>{bySubject['chemistry'] || 0}</td>
                  <td>{bySubject['biology'] || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function QuestionsCreatedChart({ buckets, isLoading, rangeLabel, isWeekly, status }: {
  buckets?: TimeseriesBucket[];
  isLoading: boolean;
  rangeLabel: string;
  isWeekly: boolean;
  status: string;
}) {
  // Aggregate into weekly buckets when the range is wide enough; otherwise
  // render the daily series as-is. Same convention the Usage tab follows.
  const rows = useMemo(() => {
    if (!buckets) return [];
    return isWeekly ? bucketWeekly(buckets) : buckets;
  }, [buckets, isWeekly]);

  const statusLabel = status === 'live' ? 'Live' : status === 'draft' ? 'Draft' : 'All';
  const total = rows.reduce((s, r) => s + r.maths + r.physics + r.chemistry, 0);

  return (
    <div className="chart-card" style={{ marginBottom: 24 }}>
      <h3>Questions Created — {statusLabel} ({rangeLabel})</h3>
      {isLoading ? (
        <p className="no-data">Loading…</p>
      ) : rows.length === 0 || total === 0 ? (
        <p className="no-data">No questions created in this range.</p>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
            <XAxis
              dataKey="date"
              tickFormatter={(v) => isWeekly ? formatWeekRange(String(v)) : formatDay(String(v))}
              tick={{ fontSize: 13 }}
              stroke="var(--text-tertiary, #9ca3af)"
              interval="preserveStartEnd"
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              stroke="var(--text-tertiary, #9ca3af)"
              allowDecimals={false}
            />
            <Tooltip
              labelFormatter={(label) => isWeekly ? formatWeekRange(String(label)) : formatDay(String(label))}
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={{ color: 'var(--text-primary)' }}
              itemStyle={{ color: 'var(--text-primary)' }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {SUBJECT_META.map(s => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stackId="subjects"
                fill={s.color}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
